import { useEffect, useState, useCallback, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import PageShell from "@/components/PageShell";
import api, { apiError } from "@/lib/api";
import { formatRupiah, formatNumber, formatDate } from "@/lib/format";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RescheduleBadge } from "@/components/StatusBadge";
import { TrancheRescheduleDialog } from "@/components/TrancheRescheduleDialog";
import RestructureDialog from "@/components/RestructureDialog";
import { TrancheProgress } from "@/components/TrancheProgress";
import { Printer, CalendarClock, ChevronRight, ChevronDown, Check } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import PayProofDialog from "@/components/PayProofDialog";
import { NotaList } from "@/components/NotaList";
import { ScheduleDates } from "@/components/ScheduleDates";
import { SupplierName } from "@/components/SupplierName";

export default function ScheduledPayments() {
  const [schedules, setSchedules] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [method, setMethod] = useState("all");
  const [expanded, setExpanded] = useState({});
  const [trDlg, setTrDlg] = useState(null);
  const [rsDlg, setRsDlg] = useState(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const canPay = user?.role !== "staff";
  const today = new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Makassar" });
  const [payDlg, setPayDlg] = useState(null);
  const markPaid = (s, t) => setPayDlg({ s, t });
  const PayBtn = ({ s, t, testid }) => canPay ? <Button size="sm" className="h-7 px-2.5 text-xs bg-blue-600 hover:bg-blue-700 text-white" onClick={(e) => { e.stopPropagation(); markPaid(s, t); }} data-testid={testid}><Check className="h-3.5 w-3.5 mr-1" />Sudah Ditransfer</Button> : null;
  const Late = ({ d }) => d < today ? <span className="ml-1.5 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-rose-100 text-rose-700" data-testid="late-badge">Terlambat</span> : null;

  const load = useCallback(() => {
    api.get("/schedules?status=active").then((r) => setSchedules(r.data));
    api.get("/invoices").then((r) => setInvoices(r.data));
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/suppliers").then((r) => setSuppliers(r.data)); }, []);

  const supName = (id) => suppliers.find((s) => s.id === id)?.name || "-";
  const invOf = (sch) => invoices.filter((i) => sch.invoice_ids.includes(i.id));
  const shown = schedules.filter((s) => method === "all" || s.method === method);
  const remaining = (s) => (s.net_amount || 0) - (s.tranches || []).filter((t) => t.cleared).reduce((a, t) => a + t.amount, 0);
  const totalNet = shown.reduce((a, s) => a + remaining(s), 0);

  return (
    <PageShell title="Pembayaran Terjadwal">
      <PayProofDialog target={payDlg} onClose={() => setPayDlg(null)} onDone={() => { setPayDlg(null); load(); }} />
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-slate-600 text-sm"><CalendarClock className="h-4 w-4" />Daftar pembayaran aktif. Giro tercatat otomatis saat cair; transfer klik "Sudah Ditransfer".</div>
        <Select value={method} onValueChange={setMethod}>
          <SelectTrigger className="w-44" data-testid="sp-method-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Metode</SelectItem>
            <SelectItem value="transfer">Transfer</SelectItem>
            <SelectItem value="giro">Bilyet Giro</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card className="shadow-sm"><CardContent className="p-0 overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Tanggal Bayar</TableHead><TableHead>Supplier</TableHead><TableHead>Jenis Pembayaran</TableHead>
            <TableHead>No. Warkat / Bank</TableHead><TableHead>Nota</TableHead><TableHead className="text-right">Roll</TableHead>
            <TableHead className="text-right">Potongan Retur</TableHead><TableHead className="text-right">Jumlah Terjadwal</TableHead><TableHead></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {shown.map((s) => {
              const invs = invOf(s);
              const isSplit = s.method === "split";
              const isOpen = !!expanded[s.id];
              return (
                <Fragment key={s.id}>
                  <TableRow className={`hover:bg-slate-50 ${isSplit ? "cursor-pointer" : ""}`} onClick={isSplit ? () => setExpanded((e) => ({ ...e, [s.id]: !isOpen })) : undefined} data-testid={`sp-row-${s.code}`}>
                    <TableCell className="font-medium"><ScheduleDates s={s} testid={`sp-dates-${s.code}`} />{s.method === "transfer" && <Late d={s.payment_date} />}{s.reschedule_badge && <RescheduleBadge />}</TableCell>
                    <TableCell><SupplierName name={supName(s.supplier_id)} id={s.supplier_id} /></TableCell>
                    <TableCell>
                      <span className="inline-flex items-center gap-1">
                        {isSplit && (isOpen ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />)}
                        {s.method === "giro" ? "Bilyet Giro" : isSplit ? `Totalan (${s.tranches?.length || 0} termin)` : "Transfer"}
                      </span>
                    </TableCell>
                    <TableCell className="text-xs">{s.method === "giro" ? `${s.warkat_number} · ${s.warkat_bank}` : isSplit ? <TrancheProgress tranches={s.tranches} testid={`sp-progress-${s.code}`} /> : "-"}</TableCell>
                    <TableCell className="text-xs"><NotaList numbers={invs.map((i) => i.invoice_number)} amounts={invs.map((i) => i.total_amount)} testid={`sp-notas-${s.code}`} /></TableCell>
                    <TableCell className="text-right font-mono">{formatNumber(invs.reduce((a, i) => a + i.total_rolls, 0))}</TableCell>
                    <TableCell className="text-right font-mono text-purple-600">{formatRupiah(s.credit_used)}</TableCell>
                    <TableCell className="text-right font-mono font-semibold">{formatRupiah(remaining(s))}{isSplit && remaining(s) !== s.net_amount && <div className="text-[11px] font-normal text-slate-400">sisa dari {formatRupiah(s.net_amount)}</div>}</TableCell>
                    <TableCell><div className="flex gap-1.5 justify-end">{(s.method === "transfer" || s.manual_hold) && !isSplit && <PayBtn s={s} testid={`sp-paid-${s.code}`} />}<Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); navigate(`/slip/${s.id}`); }} data-testid={`sp-print-${s.code}`}><Printer className="h-3.5 w-3.5 mr-1" />Slip</Button>{s.status === "active" && <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); setRsDlg(s); }} data-testid={`sp-split-${s.code}`}>{isSplit ? "Ubah Split" : "Split"}</Button>}</div></TableCell>
                  </TableRow>
                  {isSplit && isOpen && (
                    <TableRow className="bg-slate-50/60 hover:bg-slate-50/60" data-testid={`sp-tranches-${s.code}`}>
                      <TableCell colSpan={9} className="p-0">
                        <div className="px-6 py-3">
                          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Jadwal Pembayaran per Termin (BG)</div>
                          <Table>
                            <TableHeader><TableRow>
                              <TableHead className="w-16">Termin</TableHead><TableHead>Jenis</TableHead><TableHead>No. Warkat / Bank</TableHead>
                              <TableHead>Tanggal Cair</TableHead><TableHead className="text-right">Nominal</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Aksi</TableHead>
                            </TableRow></TableHeader>
                            <TableBody>
                              {(s.tranches || []).map((t, idx) => (
                                <TableRow key={t.id || idx} data-testid={`sp-tranche-${s.code}-${idx}`}>
                                  <TableCell className="font-semibold">BG {idx + 1}</TableCell>
                                  <TableCell>{t.method === "giro" ? "Bilyet Giro" : "Transfer"}</TableCell>
                                  <TableCell className="text-xs">{t.method === "giro" ? `${t.warkat_number || "-"} · ${t.warkat_bank || "-"}` : "-"}</TableCell>
                                  <TableCell>{formatDate(t.cair_date)}</TableCell>
                                  <TableCell className="text-right font-mono font-semibold">{formatRupiah(t.amount)}</TableCell>
                                  <TableCell>
                                    {t.cleared
                                      ? <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Cair · {formatDate(t.cleared_date)}</span>
                                      : <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">{t.method === "giro" ? "Terjadwal (otomatis cair)" : "Menunggu transfer"}</span>}
                                    {!t.cleared && t.method !== "giro" && <Late d={t.cair_date} />}
                                  </TableCell>
                                  <TableCell className="text-right">
                                    <div className="flex gap-1 justify-end">
                                      {!t.cleared && (t.method !== "giro" || t.manual_hold) && <PayBtn s={s} t={t} testid={`sp-tranche-paid-${s.code}-${idx}`} />}
                                      <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => navigate(`/slip/${s.id}?tranche=${idx + 1}`)} data-testid={`sp-tranche-slip-${s.code}-${idx}`}>Slip</Button>
                                      {!t.cleared && <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => setTrDlg({ schedule: s, tranche: t })} data-testid={`sp-tranche-resched-${s.code}-${idx}`}>Reschedule</Button>}
                                    </div>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              );
            })}
            {shown.length === 0 && <TableRow><TableCell colSpan={9} className="text-center text-slate-400 py-8">Belum ada pembayaran terjadwal.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </CardContent></Card>

      <div className="flex justify-end">
        <div className="total-box rounded-2xl px-5 py-3 text-right">
          <div className="text-xs text-slate-300">Total Pembayaran Terjadwal</div>
          <div className="font-mono font-bold text-xl" data-testid="sp-total">{formatRupiah(totalNet)}</div>
        </div>
      </div>

      {trDlg && <TrancheRescheduleDialog schedule={trDlg.schedule} tranche={trDlg.tranche} onClose={() => setTrDlg(null)} onSaved={() => { setTrDlg(null); load(); }} />}
      {rsDlg && <RestructureDialog schedule={rsDlg} onClose={() => setRsDlg(null)} onSaved={() => { setRsDlg(null); load(); }} />}
    </PageShell>
  );
}
